import React, {useEffect, useState} from "react";
import {
    Dialog,
    DialogContent,
    TextField,
    Typography,
    Radio,
    Checkbox,
    FormGroup,
    Box,
    FormControlLabel,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {useSaveTrend} from "../../../hooks/saveTrend";
import {SaveTrendButton} from "../../atoms/SaveTrendButton/SaveTrendButton";
import {ActiveGame} from "../../../types/nbaTypes";
import {SdqlSummaryData, Sport} from "../../../types/types";
import {useIsAdminUser} from "../../../hooks/isAdminUser/isAdminUser";

export const SaveTrendDialog = (
    {
        activeGames = [],
        isOpen,
        onClose,
        setIsSnackBarOpen,
        isSnackBarOpen,
        isTokenValid,
        sdqlSummaryData,
        queryString,
        trend,
        sport
    }:
        {
            activeGames?: ActiveGame[],
            isOpen: boolean;
            onClose: (isClosed: boolean) => void,
            isTokenValid: boolean,
            isSnackBarOpen: boolean,
            queryString: string,
            sdqlSummaryData?: { sdqlSummaryQuery: [SdqlSummaryData] },
            setIsSnackBarOpen: (isOpen: boolean) => void,
            trend: string,
            sport: Sport
        },
) => {
    const [isSuTrend, setIsSuTrend] = useState(false);
    const [isAtsTrend, setIsAtsTrend] = useState(false);
    const [isOuTrend, setIsOuTrend] = useState(false);
    const [isDefaultTrend, setIsDefaultTrend] = useState(false);
    const [isDefaultUserTrend, setIsDefaultUserTrend] = useState(false);
    const [description, setDescription] = useState('');
    const [noTrendTypeSelectedError, setNoTrendTypeSelectedError] = useState('')
    const theme = useTheme();
    const {saveTrend, error, reset} = useSaveTrend()

    const handleSubmit = async () => {
        if (!isAtsTrend && !isOuTrend && !isSuTrend) {
            setNoTrendTypeSelectedError('Please select a trend type (SU, ATS, and/or OU)')
            return;
        }
        try {
            const saveTrendResponse = await saveTrend(
                trend,
                isSuTrend,
                isAtsTrend,
                isOuTrend,
                isDefaultTrend,
                isDefaultUserTrend,
                sport,
                sdqlSummaryData?.sdqlSummaryQuery[0] || undefined,
                description,
                activeGames,
            );
            if (!saveTrendResponse?.errors?.length) {
                setIsSnackBarOpen(true)
                handleOnClose();
            }
        } catch (err) {
            console.error("Login error:", err);
        }
    };
    const { data } = useIsAdminUser()
    const { isAdminUser } = data || {};
    const isErrorPresent = !!noTrendTypeSelectedError || !isTokenValid
    const errorMessage = !isTokenValid ? 'Please log in to save trends.' : noTrendTypeSelectedError

    const ErrorComponent = isErrorPresent ? (
        <Typography
            variant="subtitle2"
            mt={1}
            style={{
                color: theme.palette.error.main,
                marginLeft: 'auto',
                marginRight: 'auto'
            }}
        >
            {errorMessage}
        </Typography>
    ) : null;

    const handleOnClose = () => {
        setIsSuTrend(false)
        setIsAtsTrend(false)
        setIsOuTrend(false)
        onClose(false)
        setDescription('')
        setNoTrendTypeSelectedError('')
        reset()
    }

    useEffect(() => {
        if (noTrendTypeSelectedError) {
            if (isSuTrend || isAtsTrend || isOuTrend) setNoTrendTypeSelectedError('')
        }

    }, [noTrendTypeSelectedError, isSuTrend, isAtsTrend, isOuTrend])

    return (
        <Dialog
            open={isOpen}
            onClose={() => handleOnClose()}
            aria-autocomplete="none"
        >
            <DialogContent
                aria-autocomplete="none"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'center',
                    minWidth: 360,
                }}
            >
                <Typography
                    variant="caption"
                    component="p"
                    style={{color: 'grey'}}
                    ml={'auto'}
                    mr={'auto'}
                >
                    Save SDQL String to My Trends:
                </Typography>
                <Typography
                    variant="subtitle1"
                    component="span"
                    align={"center"}
                >
                    {trend}
                </Typography>
                <TextField
                    onChange={
                        (event) => setDescription(event.target.value)
                    }
                    value={description}
                    style={{
                        marginTop: '8px',
                    }}
                    placeholder={'Trend description'}
                />
                {ErrorComponent}
                <Typography
                    variant="caption"
                    align="center"
                    style={{color: 'grey'}}
                    mt={1}
                >
                    Trend Type
                </Typography>
                <FormGroup
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'row',
                    }}
                >
                    <FormControlLabel
                        style={{marginLeft: '4px'}}
                        control={
                            <Checkbox
                                style={{padding: '0', paddingRight: '4px'}}
                                onChange={() => setIsSuTrend(!isSuTrend)}
                                checked={isSuTrend}
                            />
                        }
                        label="SU"
                    />
                    <FormControlLabel
                        style={{marginLeft: '4px'}}
                        control={
                            <Checkbox
                                style={{padding: '0', paddingRight: '4px'}}
                                onChange={() => setIsAtsTrend(!isAtsTrend)}
                                checked={isAtsTrend}
                            />
                        }
                        label="ATS"
                    />
                    <FormControlLabel
                        style={{marginLeft: '4px'}}
                        control={
                            <Checkbox
                                style={{padding: '0', paddingRight: '4px'}}
                                onChange={() => setIsOuTrend(!isOuTrend)}
                                checked={isOuTrend}
                            />
                        }
                        label="OU"
                    />
                        <Box width="100%" display="flex" justifyContent="center" mt={1}>
                            <FormControlLabel
                                style={{marginLeft: '4px'}}
                                control={
                                    <Checkbox
                                        style={{padding: '0', paddingRight: '4px'}}
                                        onChange={() => setIsDefaultTrend(!isDefaultTrend)}
                                        checked={isDefaultTrend}
                                    />
                                }
                                label="Homepage Trend"
                            />
                            {isAdminUser && (
                                <FormControlLabel
                                    style={{marginLeft: '4px'}}
                                    control={
                                        <Checkbox
                                            style={{padding: '0', paddingRight: '4px'}}
                                            onChange={() => setIsDefaultUserTrend(!isDefaultUserTrend)}
                                            checked={isDefaultUserTrend}
                                        />
                                    }
                                    label="Default User Trend"
                                />
                            )}
                        </Box>
                </FormGroup>
                <SaveTrendButton
                    handleSubmit={handleSubmit}
                    queryString={queryString}
                />
            </DialogContent>
        </Dialog>
    );
};
